import React from 'react';
import styled from 'styled-components';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
    
    // In production, you could send this to an error reporting service
    // Example: reportError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorContainer>
          <ErrorContent>
            <LogoImage src="/logo.png" alt="Govind Industries" />
            <h1>Oops! Something went wrong</h1>
            <p>We're sorry for the inconvenience. Please try refreshing the page.</p>
            
            <ButtonGroup>
              <RefreshButton onClick={() => window.location.reload()}>
                Refresh Page
              </RefreshButton>
              <HomeButton href="/">
                Go to Home
              </HomeButton>
            </ButtonGroup>
            
            {process.env.NODE_ENV === 'development' && (
              <ErrorDetails>
                <h3>Error Details (Development Only):</h3>
                <ErrorText>
                  {this.state.error && this.state.error.toString()}
                </ErrorText>
                <ErrorStack>
                  {this.state.errorInfo.componentStack}
                </ErrorStack>
              </ErrorDetails>
            )}
          </ErrorContent>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

// Styled Components
const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
`;

const ErrorContent = styled.div`
  text-align: center;
  max-width: 600px;
  background: white;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
`;

const LogoImage = styled.img`
  height: 60px;
  width: auto;
  margin-bottom: 1.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const RefreshButton = styled.button`
  background: ${props => props.theme?.colors?.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: #e65a00;
  }
`;

const HomeButton = styled.a`
  background: transparent;
  color: ${props => props.theme?.colors?.primary};
  border: 2px solid ${props => props.theme?.colors?.primary};
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme?.colors?.primary};
    color: white;
  }
`;

const ErrorDetails = styled.div`
  margin-top: 2rem;
  text-align: left;
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid #dc3545;
`;

const ErrorText = styled.pre`
  color: #dc3545;
  font-size: 0.9rem;
  white-space: pre-wrap;
  margin: 0.5rem 0;
`;

const ErrorStack = styled.details`
  margin-top: 1rem;
  
  summary {
    cursor: pointer;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  
  pre {
    background: #fff;
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
    font-size: 0.8rem;
    color: #666;
  }
`;

export default ErrorBoundary;